import type { ComboboxOption } from '@highereducation/hen-ui';
import type { Option } from '@de/types/types';

export interface SearchBarPartition {
  /** Options rendered inline (the configured `limit`, plus any selected option
   *  that falls outside it so it stays visible). */
  displayedOptions: Option[];
  /** Remaining options offered through the search bar. */
  searchableOptions: Option[];
}

/**
 * Splits a custom-endpoint option set into the inline-displayed options and the
 * searchable remainder for the "Enable Search Bar Options" flow.
 *
 * `displayLimitedOptions` is the already-`limit`-sliced set from
 * `useStandaloneDynamicOptions`; `allOptions` is the full fetched set. Any
 * currently selected option that is not in the displayed slice is kept visible
 * (prepended) so a selection made outside the first `limit` does not disappear.
 */
export const partitionSearchBarOptions = (
  displayLimitedOptions: Option[],
  allOptions: Option[],
  selectedOptions: Option[],
): SearchBarPartition => {
  const displayedIds = new Set(displayLimitedOptions.map((o) => o.optionId));

  const selectedNotDisplayed = selectedOptions.filter(
    (o) => !displayedIds.has(o.optionId),
  );

  const displayedOptions =
    selectedNotDisplayed.length > 0
      ? [...selectedNotDisplayed, ...displayLimitedOptions]
      : displayLimitedOptions;

  const finalDisplayedIds = new Set(displayedOptions.map((o) => o.optionId));
  const searchableOptions = allOptions.filter(
    (o) => !finalDisplayedIds.has(o.optionId),
  );

  return { displayedOptions, searchableOptions };
};

/** Maps a DE `Option` onto the hen-ui `Combobox` option shape. */
export const optionToComboboxOption = (option: Option): ComboboxOption => ({
  value: option.optionId,
  label: String(option.text),
});
